import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReturnForMethod, ModelName, StandardQueryMethodForModel } from "../types";
import type { UseTellescopeQueryOptions, UseTellescopeQueryResult } from "./types";
import { buildTellescopeQueryKey } from "../keys";
import { useTellescopeSession, useTellescopeTanstackConfigContext } from "../context";
import { executeTellescopeApiMethod } from "../utils/execute-tellescope-api-method";
import { useMemo } from "react";
import { addKeyToQueryResult, shouldCrossShareQueryCache } from "../utils";
import { seedIndividualQueryCache } from "../cache/seed-individual-query-cache";
import { resolveFromCollectionCaches } from "../cache/resolve-from-collection-caches";
import { resolveGetByIdsFromCache } from "../cache/resolve-get-by-ids-from-cache";
import { TellescopeModelFromName } from "../mappings";

/**
 * React hook for querying data from the Tellescope API using TanStack Query.
 * Only supports standard (non-paginated) query methods. Use `useTellescopeInfiniteQuery` for paginated queries.
 *
 * This hook wraps TanStack Query's `useQuery` hook with Tellescope-specific functionality,
 * providing type-safe access to API methods with automatic query key generation and caching.
 *
 * @template Model - The Tellescope model name with standard query methods (e.g., 'users', 'appointments')
 * @template Method - The standard (non-paginated) query method for the specified model (e.g., 'getOne', 'display_names')
 * @template TError - The error type (defaults to Error)
 * @template TData - The returned data type (defaults to the API method's return type)
 *
 * @param options - Query configuration options
 * @param options.model - The Tellescope model to query (e.g., 'users', 'appointments')
 * @param options.method - The standard query method to call on the model (e.g., 'getOne', 'display_names') - excludes paginated methods like 'getSome'
 * @param options.request - The request parameters for the API method
 * @param options.session - Optional Tellescope session to use instead of the context session
 * @param options...tanstackOptions - All TanStack Query options are supported except `queryKey` and `queryFn` (which are automatically generated)
 * @returns TanStack Query result object with data, loading state, error state, and refetch utilities, as well as the computed query key
 *
 * @example
 * ```tsx
 * // Query a single user
 * const { data, isLoading, error } = useTellescopeQuery({
 *   model: 'users',
 *   method: 'getOne',
 *   request: { id: 'user123' }
 * });
 *
 * // Query with TanStack Query options
 * const { data } = useTellescopeQuery({
 *   model: 'users',
 *   method: 'display_names',
 *   request: {},
 *   enabled: isReady,
 *   staleTime: 5000
 * });
 *
 * // For paginated queries, use useTellescopeInfiniteQuery instead:
 * // const { data, fetchNextPage } = useTellescopeInfiniteQuery({
 * //   model: 'users',
 * //   method: 'getSome',
 * //   request: { filter: {} }
 * // });
 * ```
 */
export const useTellescopeQuery = <
  Model extends ModelName,
  Method extends StandardQueryMethodForModel<Model>,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  session: providedSession,
  requestKeysToOmitFromQueryKey,
  requestSideEffect,
  responseSideEffect,
  crossQueryCacheSharing,
  ...tanstackOptions
}: UseTellescopeQueryOptions<Model, Method, TError, TData>): UseTellescopeQueryResult<Model, Method, TError, TData> => {
  const session = useTellescopeSession(providedSession);
  const queryClient = useQueryClient();
  const resolvedConfig = useTellescopeTanstackConfigContext({ crossQueryCacheSharing });

  const isCacheSharingEnabled = shouldCrossShareQueryCache({
    config: resolvedConfig.crossQueryCacheSharing,
    model,
    method,
  });

  const queryKey = useMemo(
    () => buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey }),
    [model, method, request, requestKeysToOmitFromQueryKey]
  );

  const userProvidedInitialData = tanstackOptions?.initialData !== undefined;

  // Resolve cross-query initialData for getOne (from collection caches) and getByIds (from individual caches)
  const crossQueryInitialData = useMemo<
    { initialData: ReturnForMethod<Model, Method>; initialDataUpdatedAt: number } | undefined
  >(() => {
    if (!isCacheSharingEnabled || userProvidedInitialData) return undefined;

    if (method === "getOne") {
      const id: string | undefined =
        typeof request === "string"
          ? request
          : typeof request === "object" && request !== null && "id" in request && typeof request.id === "string"
          ? request.id
          : undefined;
      if (!id) return undefined;

      const resolved = resolveFromCollectionCaches({ queryClient, model, id });
      if (!resolved) return undefined;
      return {
        initialData: resolved.data as ReturnForMethod<Model, Method>, // Type assert here because 'getOne' is a subtype of `ReturnForMethod...`
        initialDataUpdatedAt: resolved.dataUpdatedAt,
      };
    }

    if (method === "getByIds") {
      const ids =
        typeof request === "object" && request !== null && "ids" in request && Array.isArray(request.ids)
          ? request.ids
          : undefined;
      if (!ids || ids.length === 0) return undefined;

      const { cached, missingIds, oldestDataUpdatedAt } = resolveGetByIdsFromCache({
        queryClient,
        model,
        ids,
        staleTime: typeof tanstackOptions?.staleTime === "number" ? tanstackOptions?.staleTime : undefined,
      });

      // Only use initialData when ALL items are cached — staleTime is handled by TanStack via initialDataUpdatedAt
      if (missingIds.length === 0 && oldestDataUpdatedAt !== undefined) {
        return {
          initialData: { matches: cached } as ReturnForMethod<Model, Method>, // Type assert here because 'getByIds' is a subtype of `ReturnForMethod...`
          initialDataUpdatedAt: oldestDataUpdatedAt,
        };
      }
    }

    return undefined;
  }, [isCacheSharingEnabled, userProvidedInitialData, method, request, queryClient, model, tanstackOptions?.staleTime]);

  const initialDataOverrides = crossQueryInitialData ?? {};

  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      if (session === null) {
        throw new Error("No Tellescope session provided for query. Queries should be disabled is session is `null`");
      }

      requestSideEffect?.(request);

      // getByIds partial resolution — fetch only missing IDs, merge with cached
      if (isCacheSharingEnabled && method === "getByIds") {
        const typedRequest = request as Record<string, unknown>;
        const ids = typedRequest?.ids;

        if (Array.isArray(ids) && ids.length > 0) {
          const { cached, missingIds } = resolveGetByIdsFromCache({
            queryClient,
            model,
            ids,
            staleTime: typeof tanstackOptions?.staleTime === "number" ? tanstackOptions?.staleTime : undefined,
          });

          if (cached.length > 0 && missingIds.length > 0) {
            const partialRequest = Object.assign({}, request, { ids: missingIds }) as typeof request;
            const res = await executeTellescopeApiMethod({
              session,
              model,
              method,
              request: partialRequest,
              parseISOStrings: true,
            });

            const fetchedMatches = (res as { matches: Array<TellescopeModelFromName<Model>> }).matches ?? [];

            seedIndividualQueryCache({ queryClient, model, items: fetchedMatches });

            const merged = { matches: [...cached, ...fetchedMatches] } as ReturnForMethod<Model, Method>;
            responseSideEffect?.(request, merged);
            return merged;
          }
        }
      }

      const res = await executeTellescopeApiMethod({
        session,
        model,
        method,
        request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      });

      // Seed getOne caches from getByIds response
      if (isCacheSharingEnabled && method === "getByIds") {
        const matches = (res as { matches: Array<TellescopeModelFromName<Model>> }).matches;
        if (Array.isArray(matches)) {
          seedIndividualQueryCache({ queryClient, model, items: matches });
        }
      }

      responseSideEffect?.(request, res);
      return res;
    },
    ...tanstackOptions,
    ...initialDataOverrides,
    enabled: !!session && (tanstackOptions?.enabled ?? true),
  });

  return addKeyToQueryResult(queryResult, queryKey);
};
